/**
 * Follow-Up Engine Service
 *
 * Persists disposition rules, message templates, sequence steps,
 * and global settings to Supabase.
 * Fails gracefully — all errors are logged but never thrown,
 * so the engine works in-memory even if the DB tables don't exist yet.
 *
 * Required tables:
 *   follow_up_rules            — one row per disposition rule
 *   follow_up_templates        — message templates keyed by rule_id
 *   follow_up_sequence_steps   — ordered steps keyed by rule_id
 *   follow_up_global_settings  — singleton row (id = 'global')
 */

import { supabase } from "@/lib/supabase";
import type { DispositionRule, MessageTemplate, SequenceStep, FollowUpEngineConfig } from "@/data/FollowUpEngineContext";
import {
  toDbRule, toUiRule,
  toDbTemplate, toUiTemplate,
  toDbSequenceStep, toUiSequenceStep,
  toDbGlobalSettings, toUiGlobalSettings,
  type FollowUpRuleRow,
  type FollowUpTemplateRow,
  type FollowUpSequenceStepRow,
  type FollowUpGlobalSettingsRow,
} from "@/mappers/follow-up-engine.mapper";

export const FollowUpEngineService = {
  /**
   * Load the full engine config from DB on mount.
   * Returns null if the tables are empty or inaccessible — caller falls back to seed data.
   */
  async loadConfig(): Promise<{
    rules: DispositionRule[];
    templates: MessageTemplate[];
    sequences: SequenceStep[];
    globalSettings: FollowUpEngineConfig["globalSettings"] | null;
  } | null> {
    try {
      const [rulesRes, templatesRes, sequencesRes, settingsRes] = await Promise.all([
        supabase.from("follow_up_rules").select("*").order("created_at", { ascending: true }),
        supabase.from("follow_up_templates").select("*"),
        supabase.from("follow_up_sequence_steps").select("*").order("order_index", { ascending: true }),
        supabase.from("follow_up_global_settings").select("*").eq("id", "global").maybeSingle(),
      ]);

      if (rulesRes.error) {
        console.warn("[FollowUpEngineService.loadConfig] rules error:", rulesRes.error.message);
        return null;
      }

      const rules = ((rulesRes.data ?? []) as FollowUpRuleRow[]).map(toUiRule);

      // No rules in DB — signal caller to use seed data
      if (rules.length === 0) return null;

      const templates = templatesRes.error
        ? []
        : ((templatesRes.data ?? []) as FollowUpTemplateRow[]).map(toUiTemplate);

      const sequences = sequencesRes.error
        ? []
        : ((sequencesRes.data ?? []) as FollowUpSequenceStepRow[]).map(toUiSequenceStep);

      const globalSettings = settingsRes.data
        ? toUiGlobalSettings(settingsRes.data as FollowUpGlobalSettingsRow)
        : null;

      return { rules, templates, sequences, globalSettings };
    } catch (err) {
      console.warn("[FollowUpEngineService.loadConfig] Exception:", err);
      return null;
    }
  },

  // ── Rule operations ─────────────────────────────────────────────────────────

  async upsertRule(rule: DispositionRule): Promise<void> {
    try {
      const { error } = await supabase.from("follow_up_rules").upsert(toDbRule(rule));
      if (error) console.warn("[FollowUpEngineService.upsertRule] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.upsertRule] Exception:", err);
    }
  },

  async deleteRule(id: string): Promise<void> {
    try {
      // Cascade: remove templates and sequence steps before the rule
      await Promise.all([
        supabase.from("follow_up_templates").delete().eq("rule_id", id),
        supabase.from("follow_up_sequence_steps").delete().eq("rule_id", id),
      ]);
      const { error } = await supabase.from("follow_up_rules").delete().eq("id", id);
      if (error) console.warn("[FollowUpEngineService.deleteRule] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.deleteRule] Exception:", err);
    }
  },

  // ── Template operations ─────────────────────────────────────────────────────

  async upsertTemplate(template: MessageTemplate): Promise<void> {
    try {
      const { error } = await supabase.from("follow_up_templates").upsert(toDbTemplate(template));
      if (error) console.warn("[FollowUpEngineService.upsertTemplate] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.upsertTemplate] Exception:", err);
    }
  },

  async deleteTemplate(id: string): Promise<void> {
    try {
      const { error } = await supabase.from("follow_up_templates").delete().eq("id", id);
      if (error) console.warn("[FollowUpEngineService.deleteTemplate] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.deleteTemplate] Exception:", err);
    }
  },

  // ── Sequence step operations ────────────────────────────────────────────────

  async upsertSequenceStep(step: SequenceStep): Promise<void> {
    try {
      const { error } = await supabase.from("follow_up_sequence_steps").upsert(toDbSequenceStep(step));
      if (error) console.warn("[FollowUpEngineService.upsertSequenceStep] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.upsertSequenceStep] Exception:", err);
    }
  },

  async deleteSequenceStep(id: string): Promise<void> {
    try {
      const { error } = await supabase.from("follow_up_sequence_steps").delete().eq("id", id);
      if (error) console.warn("[FollowUpEngineService.deleteSequenceStep] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.deleteSequenceStep] Exception:", err);
    }
  },

  // ── Global settings ─────────────────────────────────────────────────────────

  async saveGlobalSettings(settings: FollowUpEngineConfig["globalSettings"]): Promise<void> {
    try {
      const { error } = await supabase
        .from("follow_up_global_settings")
        .upsert(toDbGlobalSettings(settings));
      if (error) console.warn("[FollowUpEngineService.saveGlobalSettings] DB error:", error.message);
    } catch (err) {
      console.warn("[FollowUpEngineService.saveGlobalSettings] Exception:", err);
    }
  },
};
